import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import locales from 'assets/locales.json';
import { setInvitationCode } from 'libs/shared/data/stores/auth/auth.actions';
import { isLoginResponse, isUserBusiness } from 'libs/shared/data/stores/auth/auth.reducer';
import { selectUserBusinessEntity } from 'libs/shared/data/stores/auth/auth.selectors';
import { variables } from 'libs/shared/utils/variables/variables';
import { map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UrlService {
  platformId = inject(PLATFORM_ID);

  shareUrl: string = variables.shareUrl;
  public translate = inject(TranslateService);
  private store$ = inject(Store);
  code = toSignal(
    this.store$.select(selectUserBusinessEntity).pipe(
      map((res) => {
        if (isUserBusiness(res)) {
          return (res as any).dedicated_code;
        }
        if (isLoginResponse(res)) {
          return res.dedicated_code;
        }
        return undefined;
      }),
    ),
  );

  currentLocale = toSignal(
    inject(TranslateService).onLangChange.pipe(
      map((lang) => {
        return lang.lang;
      }),
    ),
  );

  private safeBase64Encode(str: string): string {
    try {
      // First encode the string to handle special characters
      const encodedStr = encodeURIComponent(str);
      // Then convert to base64 and make it URL safe
      return btoa(encodedStr).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (error) {
      console.error('Error encoding URL:', error);
      return str;
    }
  }

  private safeBase64Decode(str: string): string {
    try {
      // Add padding if needed
      str = str.replace(/-/g, '+').replace(/_/g, '/');
      while (str.length % 4) {
        str += '=';
      }

      // Decode base64 and then URL decode
      return decodeURIComponent(atob(str));
    } catch (error) {
      console.error('Error decoding URL:', error);
      return str;
    }
  }

  /**
   *
   * @example this.urlService.generateShareUrl('profile/test', { test: 'test', test2: 'te2st' })
   * @param url test
   * @param params { test: 'test', test2: 'te2st' }
   * @returns  share/dGVzdD90ZXN0PXRlc3QmdGVzdDI9dGVzdDI=
   */
  public generateShareUrl(url: string, params: { [key: string]: string } = {}): string {
    try {
      // Remove leading slash and add query separator
      let result = url.startsWith('/') ? url.slice(1) : url;

      // Start building query parameters
      const queryParams = new URLSearchParams();

      // Add code if available
      const code = this.code();
      if (code) {
        queryParams.set(variables.inviteCode, code);
      }

      // Add additional params
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          queryParams.set(key, value);
        });
      }

      // Combine URL and query parameters
      const fullUrl = queryParams.toString() ? `${result}?${queryParams.toString()}` : result;

      // Encode the full URL using safe base64
      const encodedUrl = this.safeBase64Encode(fullUrl);
      return `${this.currentLocale() ?? 'en'}/${this.shareUrl}/${encodedUrl}`;
    } catch (error) {
      return url;
    }
  }

  /**
   *
   * @param url share/dGVzdD90ZXN0PXRlc3QmdGVzdDI9dGVzdDI=
   * @returns  test?test=test&test2=test2
   */
  public getSharedUrl(url: string): string {
    try {
      // Extract the encoded part after shareUrl
      const parts = url.split(`${this.shareUrl}/`);
      if (parts.length < 2) {
        return url;
      }

      const encodedPart = parts[1];
      if (!encodedPart) {
        return url;
      }

      // Decode the URL using safe base64
      const decodedUrl = this.safeBase64Decode(encodedPart);
      // Parse the URL to remove inviteCode parameter
      const [path, queryString] = decodedUrl.split('?');
      if (!queryString) {
        return path;
      }

      const params = new URLSearchParams(queryString);
      if (params.get(variables.inviteCode)) {
        this.store$.dispatch(
          setInvitationCode({ invitationCode: params.get(variables.inviteCode)! }),
        );
      }
      params.delete(variables.inviteCode);

      const remainingParams = params.toString();
      return remainingParams ? `${path}?${remainingParams}` : path;
    } catch (error) {
      console.error('Error decoding shared URL:', error);
      return url;
    }
  }

  hasLanguageCode(url: string): boolean {
    const regexPattern = new RegExp(`^/(${locales.locales.join('|')})`);
    return regexPattern.test(url);
  }

  getLanguageCode(url: string) {
    const regexPattern = new RegExp(`^/(${locales.locales.join('|')})`);
    const match = url.match(regexPattern);
    return match ? match[1] : null; // Returns the matched language code or null
  }

  setLanguageStore(code: any) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(variables.languageCodeStorageKey, code ?? variables.defaultLanguage);
      this.translate.use(code ?? variables.defaultLanguage);
    }
  }
}
